// Imports are same for Profile and Vertical card
import { useBlockProps, BlockControls, InnerBlocks, RichText } from "@wordpress/block-editor";
import { ToolbarGroup, Button as WPButton } from "@wordpress/components";
import CardSidePanel from "./sidePanel";
import { t } from "drupal";
import { TextControl } from "@wordpress/components";
import { useState, useEffect, useCallback } from "@wordpress/element";
import { useSelect, useDispatch } from '@wordpress/data';
import { handleUrlChange2, sanitizeInput, sanitizeKeepHTML, isValidDomain } from '../utilities/helpers';
import { createBlock } from '@wordpress/blocks';



function Edit({ attributes, setAttributes, clientId }) {

  const {
    profileName = "",
    title = "",
    content = "",
    backgroundColor = "",
    link = "",
    linkText = "",
    showLink = false,
    sizeSlug = "card_vertical_image",
    ariaLabel = '',
    titleError = "",
    profileNameError = "",
    ctaUrlError = false,
    ctaError = ""
  } = attributes;


  // console.log("Profile card edit: ", { profileName, title, content, backgroundColor, sizeSlug });


  // Update functions

  // Strip out HTML and script for title
  const onChangeTitle = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ title: cleanedText });
  };

  // Strip out HTML and script for title
  const onChangeProfileName = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ profileName: cleanedText });
  };

  // Strip out script, keep HTML for content
  const onChangeContent = (value) => {
    const cleanedText = sanitizeKeepHTML(value);
    setAttributes({ content: cleanedText });
  };

  // Strip out HTML and script for CTA
  const onChangeLink = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ link: cleanedText });
  };

  // Strip out HTML and script for CTA
  const onChangeLinkText = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ linkText: cleanedText });
  };

  // For side panel input: strip out both <script> and all HTML to avoid crashes
  const onChangeContentSidePanel = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ content: cleanedText });
  };

  // Inner blocks
  const ALLOWED_BLOCKS = ["core/image"];
  const TEMPLATE = [["core/image", {}]];
  // const PLACEHOLDER_IMAGE = 'https://deohs.washington.edu/sites/default/files/styles/faculty_bio_landing_page/public/default_images/profile_placeholder.png';
  const PLACEHOLDER_IMAGE = '/sites/default/files/styles/faculty_bio_landing_page/public/default_images/profile_placeholder.png';


  // Access the inner blocks
  const { replaceInnerBlocks } = useDispatch('core/block-editor');
  const innerBlocks = useSelect(
    (select) => select('core/block-editor').getBlocks(clientId),
    [clientId]
  );

  useEffect(() => {
    if (innerBlocks.length === 0) {
      // If no inner block exists, insert placeholder image
      const newBlock = createBlock('core/image', {
        url: PLACEHOLDER_IMAGE,
        sizeSlug: 'card_vertical_image',
      });
      replaceInnerBlocks(clientId, [newBlock], false);
    } else {
      const innerBlock = innerBlocks[0];

      if (!innerBlock.attributes.url) {
        wp.data.dispatch('core/block-editor').updateBlockAttributes(
          innerBlock.clientId,
          { url: PLACEHOLDER_IMAGE, sizeSlug: 'card_vertical_image' }
        );
      }
    }
  }, [innerBlocks, replaceInnerBlocks, clientId]);



  useEffect(() => {
    const titleError = !title?.trim();
    const profileNameError = !profileName?.trim();

    const trimmedLink = link?.trim();
    const trimmedLinkText = linkText?.trim();

    // Only set ctaUrlError if user has typed a link
    // const ctaUrlError = link?.trim() ? !isValidDomain(link) : false;

    // Only set ctaUrlError if user has typed a link
    const ctaUrlError = trimmedLink ? !isValidDomain(trimmedLink) : false;

    // Only set ctaError if user has typed a link but no CTA text
    const ctaError = trimmedLink && !trimmedLinkText;


    setAttributes({
      titleError,
      profileNameError,
      ctaUrlError,
      ctaError,
    });

    // console.log("edit.js validation state", { titleError, profileNameError, ctaError, ctaUrlError });
  }, [title, profileName, link, linkText]);


  /**
   * If the user presses Enter or Tab, update the URL
   **/

  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && link) {
      //handleUrlChange2 is from /deohs_gutenberg_blocks/blocks/utilities/helpers.js
      const updatedUrl = handleUrlChange2(link);
      if (updatedUrl !== link) { // Prevent unnecessary updates
        setAttributes({ link: updatedUrl });
      }
    }
  }, [link, setAttributes]);


  return (
    <div {...useBlockProps()}>
      <BlockControls>
        <ToolbarGroup>

        </ToolbarGroup>
      </BlockControls>

      {/* Display title message if image is missing */}
      {titleError && (
        <div className="editor-field-error-message">
          {t("A title is required for this block")}
        </div>
      )}

      {/* Display title message if image is missing */}
      {profileNameError && (
        <div className="editor-field-error-message">
          {t("A profile name is required for this block")}
        </div>
      )}


      {/* Display error message if image is missing */}
      {ctaUrlError && (
        <div className="editor-field-error-message">
          {t("If you provide a URL, please provide a valid absoloute or relative URL.")}
        </div>
      )}


      {/* Display error message if CTA is empty if URL is provided */}
      {ctaError && (
        <div className="editor-field-error-message">
          {t("CTA text is required when URL is provided")}
        </div>
      )}


      <div className="col">
        <div className="card vertical_img">

            <div class="card-body">

            <TextControl
              className={`h3 card-title h4 profileName  ${profileNameError ? "editor-field-error is-required" : ""}`}
              value={profileName}
              onChange={(value) => {
                onChangeProfileName(value, "profileName");
              }}
              placeholder={t("Profile Name")}
            />

            <TextControl
              tagName="p"
              className={`card-text title ${titleError ? "editor-field-error is-required" : ""}`}
              value={title}
              onChange={(value) => {
                onChangeTitle(value, "title");
              }}
              placeholder={t("Profile Title")}
            />

            <RichText
              tagName="p"
              className="card-text content"
              placeholder={t("Profile Content")}
              onChange={onChangeContent}
              value={content}
            />

            {/* Conditional Link Input */}
            {showLink && (
              <div>
                <TextControl
                  value={link}
                  onChange={onChangeLink}
                  className={ctaUrlError ? "editor-field-error" : ""}
                  onBlur={() => {
                    if (link?.trim()) {
                      const updatedUrl = handleUrlChange2(link.trim());
                      if (updatedUrl !== link.trim()) {
                        setAttributes({ link: updatedUrl });
                      }
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={t("/about or example.com")}
                />
                <TextControl
                  value={linkText}
                  onChange={onChangeLinkText}
                  placeholder={t("CTA Text")}
                  className={ctaError ? "editor-field-error btn btn-link" : "btn btn-link"}
                />
              </div>
            )}

          </div>
          <div>
            {/* Inner blocks for image */}
            <InnerBlocks
              template={TEMPLATE}
              allowedBlocks={ALLOWED_BLOCKS}
              templateLock="insert"
            />
          </div>
        </div>
      </div>

      {/* Same as vertical card except for showLink toggle */}
      <CardSidePanel
        profileName={profileName}
        title={title}
        content={content}
        backgroundColor={backgroundColor}
        showLink={showLink}
        link={link}
        linkText={linkText}
        onChangeProfileName={onChangeProfileName}
        onChangeTitle={onChangeTitle}
        onChangeContent={onChangeContentSidePanel}
        onChangeLink={onChangeLink}
        onChangeLinkText={onChangeLinkText}
        ariaLabel={ariaLabel}
        setAttributes={setAttributes}
      />
    </div>
  );
}

export default Edit;
